import type { PersistedState } from '@abs/shared-types';

/**
 * Serialize state to a JSON Blob for download.
 * Enhanced (Phase 3 — Step 3.10) to accept optional alerts and version the format.
 */
export function exportState(
  state: PersistedState,
  alerts?: unknown[],
): Blob {
  const payload: Record<string, unknown> = {
    ...state,
    // Include alert history if provided
    ...(alerts ? { alertHistory: alerts } : {}),
  };
  const json = JSON.stringify(payload, null, 2);
  return new Blob([json], { type: 'application/json' });
}

/**
 * Validate the structure of imported data.
 * Returns the validated PersistedState or throws an error.
 */
export function validateState(data: unknown): PersistedState {
  if (typeof data !== 'object' || data === null) {
    throw new Error('Invalid data: expected an object');
  }

  const obj = data as Record<string, unknown>;

  if (typeof obj.schemaVersion !== 'number') {
    throw new Error('Invalid data: missing or invalid schemaVersion');
  }

  if (!Array.isArray(obj.courses)) {
    throw new Error('Invalid data: courses must be an array');
  }

  if (!Array.isArray(obj.policies)) {
    throw new Error('Invalid data: policies must be an array');
  }

  if (!Array.isArray(obj.absenceEntries)) {
    throw new Error('Invalid data: absenceEntries must be an array');
  }

  if (!Array.isArray(obj.terms)) {
    throw new Error('Invalid data: terms must be an array');
  }

  if (typeof obj.settings !== 'object' || obj.settings === null) {
    throw new Error('Invalid data: settings must be an object');
  }

  const settings = obj.settings as Record<string, unknown>;
  if (!['light', 'dark', 'system'].includes(settings.theme as string)) {
    throw new Error('Invalid data: settings.theme must be "light", "dark", or "system"');
  }

  const result: PersistedState = {
    schemaVersion: obj.schemaVersion as number,
    courses: obj.courses as PersistedState['courses'],
    policies: obj.policies as PersistedState['policies'],
    absenceEntries: obj.absenceEntries as PersistedState['absenceEntries'],
    terms: obj.terms as PersistedState['terms'],
    settings: obj.settings as PersistedState['settings'],
    exportedAt: (obj.exportedAt as string) ?? new Date().toISOString(),
  };

  if (Array.isArray(obj.budgetAdjustments)) {
    result.budgetAdjustments = obj.budgetAdjustments as unknown as NonNullable<PersistedState['budgetAdjustments']>;
  }
  if (Array.isArray(obj.participationEntries)) {
    result.participationEntries = obj.participationEntries as unknown as NonNullable<PersistedState['participationEntries']>;
  }
  if (Array.isArray(obj.feedbackEntries)) {
    result.feedbackEntries = obj.feedbackEntries as unknown as NonNullable<PersistedState['feedbackEntries']>;
  }

  return result;
}

/**
 * Deserialize and validate a JSON Blob into a PersistedState.
 */
async function readBlobText(blob: Blob): Promise<string> {
  // Try native blob.text() first (modern browsers / Node 20+)
  if (typeof blob.text === 'function') {
    return blob.text();
  }
  // Fall back to Response-based reading (works in Node 18+, jsdom)
  if (typeof Response !== 'undefined') {
    return new Response(blob).text();
  }
  // Last resort: FileReader (browser-only)
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(new Error('Failed to read blob'));
    reader.readAsText(blob);
  });
}

export async function importState(blob: Blob): Promise<PersistedState> {
  const text = await readBlobText(blob);
  let data: unknown;

  try {
    data = JSON.parse(text);
  } catch {
    throw new Error('Invalid file: not valid JSON');
  }

  return validateState(data);
}

/**
 * Trigger a browser download of a Blob as a file.
 */
export function downloadBackup(blob: Blob, filename?: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename ?? `abs-backup-${new Date().toISOString().slice(0, 10)}.json`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/**
 * Open a file picker and return the selected file as a Blob.
 */
export function triggerFileUpload(): Promise<Blob> {
  return new Promise((resolve, reject) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.json,application/json';

    input.onchange = () => {
      const file = input.files?.[0];
      if (file) {
        resolve(file);
      } else {
        reject(new Error('No file selected'));
      }
    };

    input.onerror = () => reject(new Error('File upload failed'));
    input.click();
  });
}
